import { useState, useEffect } from 'react';
import { FaTrash, FaSpinner, FaPlus, FaVolumeUp } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const SingularPluralActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    words: [
      {
        singularWord: '',
        pluralWord: '',
        singularEnglish: '',
        pluralEnglish: '',
        singularAudio: null,
        pluralAudio: null,
        singularImage: null,
        pluralImage: null,
        singularEnglishAudio: null,
        pluralEnglishAudio: null
      }
    ]
  });

  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);

  const buildStoragePath = ({ rowId, fileName, field }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'singular_plural',
      fileName,
      itemId: `${rowId}-${field}`
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        if (parsed && Array.isArray(parsed.words)) {
          setContent(parsed);
        }
      } catch {
        // keep default
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const updateRow = (index, field, value) => {
    const words = [...(content.words || [])];
    words[index] = { ...(words[index] || {}), [field]: value };
    setContent(prev => ({ ...prev, words }));
  };

  const addRow = () => {
    setContent(prev => ({
      ...prev,
      words: [
        ...(prev.words || []),
        {
          singularWord: '',
          pluralWord: '',
          singularEnglish: '',
          pluralEnglish: '',
          singularAudio: null,
          pluralAudio: null,
          singularImage: null,
          pluralImage: null,
          singularEnglishAudio: null,
          pluralEnglishAudio: null
        }
      ]
    }));
  };

  const removeRow = async (index) => {
    const words = content.words || [];
    if (words.length <= 1) return;

    const row = words[index];
    const storage = getStorage();
    const fileFields = ['singularAudio', 'pluralAudio', 'singularImage', 'pluralImage', 'singularEnglishAudio', 'pluralEnglishAudio'];

    for (const field of fileFields) {
      const path = row?.[field];
      if (path) {
        try {
          const fileRef = ref(storage, path);
          await deleteObject(fileRef);
        } catch {
          // ignore delete errors
        }
      }
    }

    const updated = words.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, words: updated }));
  };

  const handleFileUpload = async (index, field, file, kind) => {
    if (!file || !chapterId || !grade) return;

    const uploadKey = `${index}-${field}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const words = content.words || [];
      const existingPath = words[index]?.[field];

      if (existingPath) {
        try {
          const existingRef = ref(storage, existingPath);
          await deleteObject(existingRef);
        } catch {
          // ignore delete
        }
      }

      const storagePath = buildStoragePath({ rowId: `row${index + 1}`, fileName: file.name, field: kind || field });
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      const updated = [...words];
      updated[index] = { ...updated[index], [field]: storagePath };
      setContent(prev => ({ ...prev, words: updated }));
    } catch {
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const previewImage = async (path) => {
    if (!path) return;
    try {
      const storage = getStorage();
      const imgRef = ref(storage, path);
      const url = await getDownloadURL(imgRef);
      window.open(url, '_blank');
    } catch (error) {
      alert(`Failed to preview image. Error: ${error.message}`);
    }
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) return;
    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioRef);
      const audio = new Audio(url);
      audio.play().catch(() => {});
    } catch (error) {
      alert(`Failed to play audio. Error: ${error.message}`);
    }
  };

  const generateAllAudio = async () => {
    const words = content.words || [];
    if (!words.length) {
      alert('No rows available to generate audio');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();

      // Build requests only for entries that are missing audio
      const singularRequests = [];
      const pluralRequests = [];
      const singularEnglishRequests = [];
      const pluralEnglishRequests = [];

      words.forEach((w, index) => {
        if (w.singularWord && w.singularWord.trim() && !w.singularAudio) {
          singularRequests.push({ index, text: w.singularWord });
        }
        if (w.pluralWord && w.pluralWord.trim() && !w.pluralAudio) {
          pluralRequests.push({ index, text: w.pluralWord });
        }
        if (w.singularEnglish && w.singularEnglish.trim() && !w.singularEnglishAudio) {
          singularEnglishRequests.push({ index, text: w.singularEnglish });
        }
        if (w.pluralEnglish && w.pluralEnglish.trim() && !w.pluralEnglishAudio) {
          pluralEnglishRequests.push({ index, text: w.pluralEnglish });
        }
      });

      if (!singularRequests.length && !pluralRequests.length && !singularEnglishRequests.length && !pluralEnglishRequests.length) {
        alert('All words already have audio. Nothing new to generate.');
        setGeneratingAudio(false);
        return;
      }

      const updatedWords = [...words];
      let audioGenerated = false;

      // Generate singular audio (Kannada)
      if (singularRequests.length) {
        const requests = singularRequests.map(({ text }) => ({
          text,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE
        }));
        const urls = await generateBatchSpeechAudio(requests, db, storage);

        singularRequests.forEach((req, i) => {
          if (i < urls.length) {
            const audioPath = urls[i];
            if (audioPath && !audioPath.startsWith('[TTS Error:')) {
              updatedWords[req.index].singularAudio = audioPath;
              audioGenerated = true;
            }
          }
        });
      }

      // Generate singular English audio
      if (singularEnglishRequests.length) {
        const requests = singularEnglishRequests.map(({ text }) => ({
          text,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE
        }));
        const urls = await generateBatchSpeechAudio(requests, db, storage);

        singularEnglishRequests.forEach((req, i) => {
          if (i < urls.length) {
            const audioPath = urls[i];
            if (audioPath && !audioPath.startsWith('[TTS Error:')) {
              updatedWords[req.index].singularEnglishAudio = audioPath;
              audioGenerated = true;
            }
          }
        });
      }

      // Generate plural English audio
      if (pluralEnglishRequests.length) {
        const requests = pluralEnglishRequests.map(({ text }) => ({
          text,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE
        }));
        const urls = await generateBatchSpeechAudio(requests, db, storage);

        pluralEnglishRequests.forEach((req, i) => {
          if (i < urls.length) {
            const audioPath = urls[i];
            if (audioPath && !audioPath.startsWith('[TTS Error:')) {
              updatedWords[req.index].pluralEnglishAudio = audioPath;
              audioGenerated = true;
            }
          }
        });
      }

      // Generate plural audio (Kannada)
      if (pluralRequests.length) {
        const requests = pluralRequests.map(({ text }) => ({
          text,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE
        }));
        const urls = await generateBatchSpeechAudio(requests, db, storage);

        pluralRequests.forEach((req, i) => {
          if (i < urls.length) {
            const audioPath = urls[i];
            if (audioPath && !audioPath.startsWith('[TTS Error:')) {
              updatedWords[req.index].pluralAudio = audioPath;
              audioGenerated = true;
            }
          }
        });
      }

      setContent(prev => ({ ...prev, words: updatedWords }));

      if (audioGenerated) {
        alert('Audio generated successfully for all words!');
      } else {
        alert('Failed to generate audio. Please check the words and try again.');
      }
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Singular / Plural (ಏಕವಚನ / ಬಹುವಚನ)</h3>
            <p className="text-sm text-gray-600 mt-1">
              Add singular and plural words with their English equivalents, audio, and images.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm text-sm disabled:bg-emerald-300 disabled:cursor-not-allowed"
              onClick={generateAllAudio}
              disabled={generatingAudio || !(content.words && content.words.length)}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Audio
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm text-sm"
              onClick={addRow}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Row
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {(content.words || []).map((row, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {idx + 1}
                </div>

                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Singular (ಏಕವಚನ) *"
                    value={row.singularWord || ''}
                    onChange={e => updateRow(idx, 'singularWord', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Plural (ಬಹುವಚನ) *"
                    value={row.pluralWord || ''}
                    onChange={e => updateRow(idx, 'pluralWord', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Singular English"
                    value={row.singularEnglish || ''}
                    onChange={e => updateRow(idx, 'singularEnglish', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Plural English"
                    value={row.pluralEnglish || ''}
                    onChange={e => updateRow(idx, 'pluralEnglish', e.target.value)}
                  />
                </div>

                {/* Images */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                  {/* Singular image */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`sp-singular-img-${idx}`}
                      onChange={e => handleFileUpload(idx, 'singularImage', e.target.files[0], 'singular')}
                      disabled={uploadingFiles[`${idx}-singularImage`]}
                    />
                    <label
                      htmlFor={`sp-singular-img-${idx}`}
                      className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                    >
                      🖼️ Singular
                    </label>
                    {uploadingFiles[`${idx}-singularImage`] && (
                      <span className="text-purple-600 text-[10px]">Uploading...</span>
                    )}
                    {row.singularImage && !uploadingFiles[`${idx}-singularImage`] && (
                      <button
                        type="button"
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        onClick={() => previewImage(row.singularImage)}
                      >
                        View
                      </button>
                    )}
                  </div>

                  {/* Plural image */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`sp-plural-img-${idx}`}
                      onChange={e => handleFileUpload(idx, 'pluralImage', e.target.files[0], 'plural')}
                      disabled={uploadingFiles[`${idx}-pluralImage`]}
                    />
                    <label
                      htmlFor={`sp-plural-img-${idx}`}
                      className="cursor-pointer px-2 py-1 bg-pink-50 text-pink-700 rounded text-xs hover:bg-pink-100 transition-colors"
                    >
                      🖼️ Plural
                    </label>
                    {uploadingFiles[`${idx}-pluralImage`] && (
                      <span className="text-pink-600 text-[10px]">Uploading...</span>
                    )}
                    {row.pluralImage && !uploadingFiles[`${idx}-pluralImage`] && (
                      <button
                        type="button"
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        onClick={() => previewImage(row.pluralImage)}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>

                {/* Audio controls */}
                <div className="flex flex-col items-start gap-2 text-xs text-gray-600">
                  {/* English audio line: Singular Eng + Plural Eng */}
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Singular English audio */}
                    <div className="flex items-center gap-1">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`sp-singular-en-audio-${idx}`}
                        onChange={e => handleFileUpload(idx, 'singularEnglishAudio', e.target.files[0], 'singular_en')}
                        disabled={uploadingFiles[`${idx}-singularEnglishAudio`]}
                      />
                      <label
                        htmlFor={`sp-singular-en-audio-${idx}`}
                        className="cursor-pointer px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-xs hover:bg-yellow-100 transition-colors"
                      >
                        🎵 Singular Eng
                      </label>
                      {uploadingFiles[`${idx}-singularEnglishAudio`] && (
                        <FaSpinner className="w-3 h-3 animate-spin text-yellow-600" />
                      )}
                      {row.singularEnglishAudio && !uploadingFiles[`${idx}-singularEnglishAudio`] && (
                        <>
                          <button
                            type="button"
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            onClick={() => playAudio(row.singularEnglishAudio)}
                          >
                            <FaVolumeUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => updateRow(idx, 'singularEnglishAudio', null)}
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>

                    {/* Plural English audio */}
                    <div className="flex items-center gap-1">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`sp-plural-en-audio-${idx}`}
                        onChange={e => handleFileUpload(idx, 'pluralEnglishAudio', e.target.files[0], 'plural_en')}
                        disabled={uploadingFiles[`${idx}-pluralEnglishAudio`]}
                      />
                      <label
                        htmlFor={`sp-plural-en-audio-${idx}`}
                        className="cursor-pointer px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs hover:bg-indigo-100 transition-colors"
                      >
                        🎵 Plural Eng
                      </label>
                      {uploadingFiles[`${idx}-pluralEnglishAudio`] && (
                        <FaSpinner className="w-3 h-3 animate-spin text-indigo-600" />
                      )}
                      {row.pluralEnglishAudio && !uploadingFiles[`${idx}-pluralEnglishAudio`] && (
                        <>
                          <button
                            type="button"
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            onClick={() => playAudio(row.pluralEnglishAudio)}
                          >
                            <FaVolumeUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => updateRow(idx, 'pluralEnglishAudio', null)}
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Kannada audio line: Singular + Plural */}
                  <div className="flex flex-wrap items-center gap-3 mt-1">
                    <div className="flex items-center gap-1">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`sp-singular-audio-${idx}`}
                        onChange={e => handleFileUpload(idx, 'singularAudio', e.target.files[0], 'singular')}
                        disabled={uploadingFiles[`${idx}-singularAudio`]}
                      />
                      <label
                        htmlFor={`sp-singular-audio-${idx}`}
                        className="cursor-pointer px-2 py-1 bg-green-50 text-green-700 rounded text-xs hover:bg-green-100 transition-colors"
                      >
                        🎵 Singular
                      </label>
                      {uploadingFiles[`${idx}-singularAudio`] && (
                        <FaSpinner className="w-3 h-3 animate-spin text-green-600" />
                      )}
                      {row.singularAudio && !uploadingFiles[`${idx}-singularAudio`] && (
                        <>
                          <button
                            type="button"
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            onClick={() => playAudio(row.singularAudio)}
                          >
                            <FaVolumeUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => updateRow(idx, 'singularAudio', null)}
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>

                    {/* Plural audio */}
                    <div className="flex items-center gap-1">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`sp-plural-audio-${idx}`}
                        onChange={e => handleFileUpload(idx, 'pluralAudio', e.target.files[0], 'plural')}
                        disabled={uploadingFiles[`${idx}-pluralAudio`]}
                      />
                      <label
                        htmlFor={`sp-plural-audio-${idx}`}
                        className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      >
                        🎵 Plural
                      </label>
                      {uploadingFiles[`${idx}-pluralAudio`] && (
                        <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />
                      )}
                      {row.pluralAudio && !uploadingFiles[`${idx}-pluralAudio`] && (
                        <>
                          <button
                            type="button"
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            onClick={() => playAudio(row.pluralAudio)}
                          >
                            <FaVolumeUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => updateRow(idx, 'pluralAudio', null)}
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {(content.words || []).length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removeRow(idx)}
                    title="Remove row"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const validateSingularPluralContent = (content) => {
  const errors = [];

  if (!content || typeof content !== 'object') {
    errors.push('Content must be an object');
    return errors;
  }

  if (!content.words || !Array.isArray(content.words)) {
    errors.push('Content must have a words array');
    return errors;
  }

  if (content.words.length === 0) {
    errors.push('At least one row is required');
    return errors;
  }

  content.words.forEach((row, index) => {
    if (!row.singularWord || !row.singularWord.trim()) {
      errors.push(`Row ${index + 1}: Singular word is required`);
    }
    if (!row.pluralWord || !row.pluralWord.trim()) {
      errors.push(`Row ${index + 1}: Plural word is required`);
    }
  });

  return errors;
};

export default SingularPluralActivity;
